import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { CASINO_VENDORS, SLOT_VENDORS, CRASH_VENDORS } from "../../data/site";
import { useGameLaunch } from "../../hooks/useGameLaunch";
import { safeFetch } from "../../lib/safeFetch";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Bundled fallback (de-duped) shown when the admin-managed providers list is empty.
const FALLBACK_PROVIDERS = Array.from(
  new Map(
    [
      ...SLOT_VENDORS.slice(0, 8),
      ...CASINO_VENDORS.slice(0, 5),
      ...CRASH_VENDORS.slice(0, 4),
    ].map((p) => [p.id, p])
  ).values()
);

const ProviderTile = ({ p, onLaunch }) => (
  <button
    type="button"
    onClick={() => onLaunch(p.game_uid || p.id, p.label, p.provider || p.label)}
    data-testid={`provider-tile-${p.id}`}
    className="shrink-0 group rounded-xl bg-[#161e2a] hover:bg-[#1d2735] border border-white/5 hover:border-white/15 w-[140px] sm:w-[160px] h-[100px] sm:h-[120px] flex flex-col items-center justify-center gap-2 p-3 transition-all relative overflow-hidden"
  >
    <div
      aria-hidden
      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
      style={{
        background:
          "radial-gradient(120% 100% at 50% 100%, rgba(25,165,92,0.18), transparent 70%)",
      }}
    />
    <img src={p.icon} alt={p.label} className="h-9 sm:h-11 w-auto relative z-10" />
    <span className="text-xs sm:text-sm text-white/85 relative z-10 font-semibold">
      {p.label}
    </span>
  </button>
);

export const ProvidersCarousel = () => {
  const scrollRef = useRef(null);
  const { launchGame, modal: launchModal } = useGameLaunch();
  const [items, setItems] = useState(FALLBACK_PROVIDERS);

  // Pull the admin-managed set from the public providers endpoint.
  // We fetch ALL categories (no filter) so every provider added in admin UI
  // — across casino, slots, crash, table, fishing, arcade, lottery, sports —
  // shows up here. Cache-busted so admin changes appear on next page load.
  const reload = () => {
    safeFetch(`${BACKEND_URL}/api/public/providers?_t=${Date.now()}`, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : []))
      .catch(() => [])
      .then((list) => {
        if (!Array.isArray(list) || !list.length) return;
        // Sort by admin-controlled `order` (asc), then created_at desc
        const sorted = [...list].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        const deduped = Array.from(
          new Map(
            sorted.map((p) => [
              p.slug || p.id,
              {
                id: p.slug || p.id,
                label: p.name,
                icon: p.icon,
                // Admin-set game code takes precedence over slug for launch
                game_uid: p.game_uid || "",
                link: p.link || "",
              },
            ])
          ).values()
        );
        setItems(deduped);
      });
  };

  useEffect(() => {
    reload();
    // No focus / visibilitychange auto-reload — was causing the providers
    // carousel to silently re-fetch on every tab switch, contributing to
    // the "page keeps refreshing" feel. Admin edits will surface on the
    // next genuine navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scroll = (dir) => {
    if (!scrollRef.current) return;
    const w = scrollRef.current.clientWidth;
    scrollRef.current.scrollBy({ left: dir * w * 0.8, behavior: "smooth" });
  };

  return (
    <section className="py-5 sm:py-6" data-testid="providers-carousel">
      <div className="flex items-center justify-between mb-3 sm:mb-4">
        <h3 className="flex items-center gap-2 sm:gap-3 text-lg sm:text-2xl font-bold text-white">
          <span className="h-5 sm:h-6 w-1 rounded-full bg-[#19a55c]" />
          Providers
        </h3>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => scroll(-1)}
            data-testid="providers-prev"
            aria-label="prev"
            className="h-8 w-8 sm:h-9 sm:w-9 rounded-full bg-[#1a2030] hover:bg-[#222a3d] flex items-center justify-center text-white/80"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            type="button"
            onClick={() => scroll(1)}
            data-testid="providers-next"
            aria-label="next"
            className="h-8 w-8 sm:h-9 sm:w-9 rounded-full bg-[#1a2030] hover:bg-[#222a3d] flex items-center justify-center text-white/80"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
      <div
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin -mx-3 sm:-mx-6 px-3 sm:px-6 snap-x"
      >
        {items.map((p) => (
          <div key={p.id} className="snap-start">
            <ProviderTile p={p} onLaunch={launchGame} />
          </div>
        ))}
      </div>
      {launchModal}
    </section>
  );
};
